import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Input from "../../../shared/Input";
import useForm from "../../../hooks/useForm";
import { Button, Modal, Select } from "../../../shared";
import { useAddCustomerMutation } from "../../../redux/services/customers/customersApi";
import { camelCaseToSpaces } from "../../../utils";

export const CreateCustomerModal = ({ visible, setVisible, onCancel }) => {
  const navigate = useNavigate();
  const { form, updateForm, clearForm } = useForm();
  const [addCustomer, { isLoading }] = useAddCustomerMutation();

  function closeModal() {
    clearForm();
    onCancel && onCancel(false);
    setVisible(false);
  }

  const requiredFieldLabels = useMemo(
    () => ({
      name: "Company Name",
      email: "Company Email",
      phone: "Company Phone",
      appType: "Application Type",
      industry: "Industry",
      ownerFirstName: "Owner First Name",
      ownerLastName: "Owner Last Name",
      ownerEmail: "Owner Email",
      ownerPhone: "Owner Phone",
    }),
    []
  );

  async function handleSave() {
    const missingFields = Object.entries(requiredFieldLabels)
      .filter(([key]) => !String(form[key] || "").trim())
      .map(([, label]) => label);

    if (missingFields.length > 0) {
      toast(`Please complete: ${missingFields.join(", ")}`, {
        toastId: "add-customer-missing-fields",
        type: "warning",
      });
      return;
    }

    try {
      const payload = {
        name: form.name?.trim(),
        email: form.email?.trim(),
        phone: form.phone?.trim(),
        appType: form.appType,
        industry: form.industry?.trim(),
        owner: {
          firstName: form.ownerFirstName?.trim(),
          lastName: form.ownerLastName?.trim(),
          email: form.ownerEmail?.trim(),
          phone: form.ownerPhone?.trim(),
        },
      };

      const res = await addCustomer(payload).unwrap();

      toast(res.message || "Customer created and onboarding email sent", {
        toastId: "add-customer-" + res.status,
        type: res.status,
      });

      if (res.status === "success") {
        navigate("/admin/customers/" + res.data._id);
        closeModal();
      }
    } catch (error) {
      toast(error?.data?.message || "Unable to create customer", {
        toastId: "add-customer-error",
        type: "error",
      });
    }
  }

  const footer = (
    <div>
      <Button type="alternative" className="mr-2" onClick={closeModal}>
        Cancel
      </Button>
      <Button
        id="submit-customer"
        loading={isLoading}
        disabled={isLoading}
        onClick={handleSave}
      >
        Submit
      </Button>
    </div>
  );

  const content = (
    <div className="grid grid-cols-1 gap-3">
      <div>
        <h3 className="text-base font-semibold text-gray-900">Company</h3>
      </div>
      <div>
        <Input
          id="name"
          value={form.name}
          label="Company Name"
          placeholder="Company Name"
          onChange={updateForm}
          required
        />
      </div>
      <div>
        <Input
          id="email"
          value={form.email}
          label="Email"
          placeholder="Email"
          onChange={updateForm}
          required
        />
      </div>
      <div>
        <Input
          id="phone"
          value={form.phone}
          label="Phone"
          placeholder="Phone"
          onChange={updateForm}
          required
        />
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Select
          id="appType"
          value={camelCaseToSpaces(form.appType)}
          label="Application Type"
          placeholder="Select Application Type"
          onChange={updateForm}
          options={[
            { value: "trucking", label: "Trucking" },
            { value: "serviceRepair", label: "Service Repair" },
          ]}
          required
        />
        <Input
          id="industry"
          value={form.industry}
          label="Industry"
          placeholder="Industry"
          onChange={updateForm}
          required
        />
      </div>
      <div className="pt-2">
        <h3 className="text-base font-semibold text-gray-900">Primary Owner</h3>
        <p className="text-sm text-gray-500">
          This person will receive the onboarding email and finish account setup.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Input
          id="ownerFirstName"
          value={form.ownerFirstName}
          label="First Name"
          placeholder="First Name"
          onChange={updateForm}
          required
        />
        <Input
          id="ownerLastName"
          value={form.ownerLastName}
          label="Last Name"
          placeholder="Last Name"
          onChange={updateForm}
          required
        />
      </div>
      <div>
        <Input
          id="ownerEmail"
          value={form.ownerEmail}
          label="Email"
          placeholder="Owner Email"
          onChange={updateForm}
          required
        />
      </div>
      <div>
        <Input
          id="ownerPhone"
          value={form.ownerPhone}
          label="Phone"
          placeholder="Owner Phone"
          onChange={updateForm}
          required
        />
      </div>
    </div>
  );

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      onClose={closeModal}
      title="Create Customer"
      footer={footer}
      content={content}
      size="sm"
      footerPosition="right"
      contentWrapperclassName="p-3 lg:p-6 space-y-6"
    />
  );
};
